import React, { useState, useEffect, useRef, useCallback } from 'react';
import { FiClock, FiCalendar, FiChevronDown, FiCheck } from 'react-icons/fi';

const formatDateBR = (dateStr) => {
  if (!dateStr) return '';
  try {
    const clean = String(dateStr).split('T')[0].split(' ')[0];
    const parts = clean.split('-');
    if (parts.length === 3) {
      return `${parts[2]}/${parts[1]}/${parts[0]}`;
    }
    const d = new Date(dateStr);
    return isNaN(d.getTime()) ? dateStr : d.toLocaleDateString('pt-BR');
  } catch (_) {
    return dateStr;
  }
};

/**
 * BIPeriodFilter
 * Componente de seleção de intervalo de datas e atalhos rápidos com o design idêntico ao BI Metas.
 */
export default function BIPeriodFilter({
  startDate = '',
  endDate = '',
  onChange,
  label = 'Início / Fim',
  minWidth = '190px',
  allowClear = true,
  style = {}
}) {
  const containerRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);
  const [tempStartDate, setTempStartDate] = useState(startDate);
  const [tempEndDate, setTempEndDate] = useState(endDate);
  const [selectedMonth, setSelectedMonth] = useState('');

  // Sincroniza estado temporário quando o popover é aberto ou as props mudam
  useEffect(() => {
    setTempStartDate(startDate);
    setTempEndDate(endDate);
    if (startDate) {
      const parts = startDate.split('-');
      if (parts.length >= 2) {
        setSelectedMonth(`${parts[0]}-${parts[1]}`);
      }
    }
  }, [startDate, endDate, isOpen]);

  // Fechar ao clicar fora
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  // Fechar com ESC
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && isOpen) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      window.addEventListener('keydown', handleKeyDown);
    }
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen]);

  const handleApply = useCallback((start, end) => {
    setIsOpen(false);
    if (onChange) {
      onChange(start, end);
    }
  }, [onChange]);

  // Atalhos rápidos
  const handleQuickPreset = (preset) => {
    const now = new Date();
    let refYear = now.getFullYear();
    let refMonth = now.getMonth() + 1;

    if (tempStartDate) {
      const parts = tempStartDate.split('-');
      if (parts.length >= 2) {
        refYear = parseInt(parts[0], 10);
        refMonth = parseInt(parts[1], 10);
      }
    }

    if (preset === 'month') {
      const start = `${refYear}-${String(refMonth).padStart(2, '0')}-01`;
      const lastD = new Date(refYear, refMonth, 0).getDate();
      const end = `${refYear}-${String(refMonth).padStart(2, '0')}-${String(lastD).padStart(2, '0')}`;
      setTempStartDate(start);
      setTempEndDate(end);
      handleApply(start, end);
    } else if (preset === '1q') {
      const start = `${refYear}-${String(refMonth).padStart(2, '0')}-01`;
      const end = `${refYear}-${String(refMonth).padStart(2, '0')}-15`;
      setTempStartDate(start);
      setTempEndDate(end);
      handleApply(start, end);
    } else if (preset === '2q') {
      const lastD = new Date(refYear, refMonth, 0).getDate();
      const start = `${refYear}-${String(refMonth).padStart(2, '0')}-16`;
      const end = `${refYear}-${String(refMonth).padStart(2, '0')}-${String(lastD).padStart(2, '0')}`;
      setTempStartDate(start);
      setTempEndDate(end);
      handleApply(start, end);
    } else if (preset === 'yesterday') {
      const yesterday = new Date();
      yesterday.setDate(yesterday.getDate() - 1);
      const yYear = yesterday.getFullYear();
      const yMonth = yesterday.getMonth() + 1;
      const yDay = yesterday.getDate();
      const start = `${yYear}-${String(yMonth).padStart(2, '0')}-01`;
      const end = `${yYear}-${String(yMonth).padStart(2, '0')}-${String(yDay).padStart(2, '0')}`;
      setTempStartDate(start);
      setTempEndDate(end);
      handleApply(start, end);
    } else if (preset === 'last_month') {
      const lastMonthDate = new Date(refYear, refMonth - 2, 1);
      const lmYear = lastMonthDate.getFullYear();
      const lmMonth = lastMonthDate.getMonth() + 1;
      const lastD = new Date(lmYear, lmMonth, 0).getDate();
      const start = `${lmYear}-${String(lmMonth).padStart(2, '0')}-01`;
      const end = `${lmYear}-${String(lmMonth).padStart(2, '0')}-${String(lastD).padStart(2, '0')}`;
      setTempStartDate(start);
      setTempEndDate(end);
      setSelectedMonth(`${lmYear}-${String(lmMonth).padStart(2, '0')}`);
      handleApply(start, end);
    } else if (preset === 'clear') {
      setTempStartDate('');
      setTempEndDate('');
      setSelectedMonth('');
      handleApply('', '');
    }
  };

  const handleMonthPickerChange = (val) => {
    setSelectedMonth(val);
    if (!val) return;
    const [yearStr, monthStr] = val.split('-');
    const y = parseInt(yearStr, 10);
    const m = parseInt(monthStr, 10);
    const lastDay = new Date(y, m, 0).getDate();
    const start = `${y}-${String(m).padStart(2, '0')}-01`;
    const end = `${y}-${String(m).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
    setTempStartDate(start);
    setTempEndDate(end);
  };

  const hasPeriod = Boolean(startDate || endDate);
  const displayText = hasPeriod
    ? `${formatDateBR(startDate)} a ${formatDateBR(endDate)}`
    : 'Selecione o período';

  return (
    <div className="bi-filter-dropdown-container" ref={containerRef} style={{ minWidth, ...style }}>
      {label && <span className="bi-filter-label">{label}</span>}
      <div
        className={`bi-filter-trigger-box ${isOpen ? 'open' : ''} ${hasPeriod ? 'active' : ''}`}
        onClick={() => setIsOpen(!isOpen)}
        title="Configurar intervalo ou atalhos de período"
      >
        <div className="bi-filter-trigger-content">
          <FiClock size={13} className="text-primary opacity-75" />
          <span style={{ fontSize: '0.78rem' }}>
            {displayText}
          </span>
        </div>
        <FiChevronDown size={14} className={`bi-filter-chevron ${isOpen ? 'open' : ''}`} />
      </div>

      {isOpen && (
        <div className="bi-filter-popover-panel date-panel p-3" style={{ zIndex: 1070 }}>
          <div className="fw-bold text-dark small mb-2 d-flex align-items-center justify-content-between">
            <span className="d-flex align-items-center gap-1">
              <FiClock size={14} className="text-primary" /> Intervalo Customizado
            </span>
            {allowClear && hasPeriod && (
              <button
                type="button"
                className="btn btn-link btn-sm text-danger p-0 text-decoration-none"
                style={{ fontSize: '0.74rem' }}
                onClick={() => handleQuickPreset('clear')}
              >
                Limpar datas
              </button>
            )}
          </div>

          <div className="d-flex align-items-center gap-2 mb-2">
            <div style={{ flex: 1, minWidth: 0 }}>
              <label className="text-muted mb-1" style={{ fontSize: '0.72rem' }}>De:</label>
              <input
                type="date"
                className="bi-input form-control form-control-sm w-100"
                value={tempStartDate}
                onChange={(e) => setTempStartDate(e.target.value)}
              />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <label className="text-muted mb-1" style={{ fontSize: '0.72rem' }}>Até:</label>
              <input
                type="date"
                className="bi-input form-control form-control-sm w-100"
                value={tempEndDate}
                onChange={(e) => setTempEndDate(e.target.value)}
              />
            </div>
          </div>

          {/* Atalho de seleção de mês */}
          <div className="mb-3">
            <label className="text-muted mb-1 d-flex align-items-center gap-1" style={{ fontSize: '0.72rem' }}>
              <FiCalendar size={12} /> Ou selecione o mês completo:
            </label>
            <input
              type="month"
              className="bi-input form-control form-control-sm w-100"
              value={selectedMonth}
              onChange={(e) => handleMonthPickerChange(e.target.value)}
            />
          </div>

          <div className="fw-bold text-dark small mb-1.5">Atalhos Rápidos de Período:</div>
          <div className="d-flex flex-column gap-1 mb-3">
            <button
              type="button"
              className="btn btn-sm btn-light text-start border py-1 px-2"
              style={{ fontSize: '0.76rem' }}
              onClick={() => handleQuickPreset('month')}
            >
              📅 Todo o Mês
            </button>
            <button
              type="button"
              className="btn btn-sm btn-light text-start border py-1 px-2"
              style={{ fontSize: '0.76rem' }}
              onClick={() => handleQuickPreset('1q')}
            >
              🌓 1ª Quinzena (Dia 01 ao 15)
            </button>
            <button
              type="button"
              className="btn btn-sm btn-light text-start border py-1 px-2"
              style={{ fontSize: '0.76rem' }}
              onClick={() => handleQuickPreset('2q')}
            >
              🌗 2ª Quinzena (Dia 16 ao Fim)
            </button>
            <button
              type="button"
              className="btn btn-sm btn-light text-start border py-1 px-2"
              style={{ fontSize: '0.76rem' }}
              onClick={() => handleQuickPreset('yesterday')}
            >
              ⏳ Até Ontem (Dias Fechados)
            </button>
            <button
              type="button"
              className="btn btn-sm btn-light text-start border py-1 px-2"
              style={{ fontSize: '0.76rem' }}
              onClick={() => handleQuickPreset('last_month')}
            >
              🔙 Mês Anterior
            </button>
          </div>

          <div className="d-flex justify-content-between align-items-center pt-2 border-top">
            <button
              type="button"
              className="btn btn-sm btn-light border px-2"
              style={{ fontSize: '0.78rem' }}
              onClick={() => setIsOpen(false)}
            >
              Cancelar
            </button>
            <button
              type="button"
              className="btn btn-sm btn-primary px-3 d-flex align-items-center gap-1"
              style={{ fontSize: '0.78rem' }}
              onClick={() => handleApply(tempStartDate, tempEndDate)}
            >
              <FiCheck size={13} /> Aplicar Período
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
